import flatpickr from 'flatpickr';
import HttpCodes from 'http-status-codes';
import i18next from 'i18next';
import Backend from 'i18next-chained-backend';
import HttpBackend from 'i18next-http-backend';
import _ from 'lodash';
import moment from 'moment-timezone';
import { initReactI18next } from 'react-i18next';
import { sqWorkbenchStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { applyDateFormatPreference } from '@/datetime/dateFormatPreference.utilities';
import { LOCALES, LOCALE_DATA, NAMESPACED_LOCALE_DATA } from '@/utilities/i18n.constants';
import { setAgGridLocale, setDateFnsLocale as setStoreLocale } from '@/workbench/workbench.actions';
import { Highcharts, type LangOptions } from '@seeqdev/highcharts';
import { I18N_NAMESPACE as QOMPONENTS_I18N_NAMESPACE } from '@seeqdev/qomponents';
import type { HttpBackendOptions } from 'i18next-http-backend';
import type { LocaleKey } from '@/utilities/i18n.constants';

const DEFAULT_I18N_NAMESPACE = 'webserver';

const getTranslationPath = (language: string, namespace: string) => {
  const namespaceLocaleData = getNamespacedLocaleData(namespace);
  if (namespaceLocaleData) {
    return (
      _.find(namespaceLocaleData, ({ key }) => key === language)?.translationPath ??
      _.find(namespaceLocaleData, ({ key }) => key === LOCALES.EN)?.translationPath
    );
  }

  return _.find(LOCALE_DATA, ({ key }) => key === language)?.translationPath;
};

export const getNamespacedLocaleData = (namespace: string) => NAMESPACED_LOCALE_DATA[namespace];

const setDateTimeLocales = async (browserLanguage: string) => {
  const results = await Promise.allSettled([
    setMomentLocale(browserLanguage),
    setDateFnsLocale(browserLanguage),
    setFlatpickrLocale(browserLanguage),
  ]);
  results.forEach((result, index) => {
    if (result.status === 'rejected') {
      const localeSetters = ['moment', 'date-fns', 'flatpickr'];
      console.warn(`Failed to set ${localeSetters[index]} locale to "${browserLanguage}":`, result.reason);
    }
  });
};

const setMomentLocale = async (browserLanguage: string) => {
  const momentLocaleLoader = MOMENT_LOCALE_DATA[browserLanguage];
  if (momentLocaleLoader) {
    const momentLocaleResponse = await momentLocaleLoader();
    const config = momentLocaleResponse.default._config;

    if (moment.locales().includes(browserLanguage)) {
      moment.updateLocale(browserLanguage, config);
    } else {
      moment.defineLocale(browserLanguage, config);
    }
    moment.locale(browserLanguage);
    // Loading the locale above resets longDateFormat to native, wiping any override — re-assert it.
    applyDateFormatPreference(sqWorkbenchStore.dateFormat);
  }
};

const setFlatpickrLocale = async (browserLanguage: string) => {
  const flatpickrLocaleLoader = FLATPICKR_LOCALE_DATA[browserLanguage];
  if (flatpickrLocaleLoader) {
    const flatpickrLocaleResponse = await flatpickrLocaleLoader();
    const locale = flatpickrLocaleResponse?.default?.[browserLanguage];
    flatpickr.localize(locale);
  }
};

const setDateFnsLocale = async (browserLanguage: string) => {
  const dateFnsLoader = DATE_FNS_LOCALE_DATA[browserLanguage];
  if (dateFnsLoader) {
    const localeData = await dateFnsLoader();
    setStoreLocale(localeData);
  }
};

export async function init18n() {
  const browserLanguage = getBrowserLanguage().toLowerCase();

  if (browserLanguage !== 'en' && browserLanguage !== 'en-us') {
    await setDateTimeLocales(browserLanguage);
  }

  let manifest: Record<string, Record<string, string>> = {};
  const remoteBackend: HttpBackendOptions = {
    loadPath: async ([language]: string[], [namespace = DEFAULT_I18N_NAMESPACE]: string[]) => {
      manifest = await fetchI18nManifest(manifest);
      return manifest[namespace]?.[language] ?? getTranslationPath(language, namespace)!;
    },
  };
  const localBackend: HttpBackendOptions = {
    loadPath: ([language]: string[], [namespace = DEFAULT_I18N_NAMESPACE]: string[]) =>
      getTranslationPath(language, namespace)!,
  };

  return i18next
    .use(Backend)
    .use(initReactI18next)
    .init({
      lng: 'en',
      fallbackLng: 'en',
      defaultNS: DEFAULT_I18N_NAMESPACE,
      ns: [DEFAULT_I18N_NAMESPACE, QOMPONENTS_I18N_NAMESPACE],
      compatibilityJSON: 'v3',
      supportedLngs: _.values(LOCALES),
      nonExplicitSupportedLngs: false,
      interpolation: {
        skipOnVariables: true,
        escapeValue: false,
      },
      backend: {
        backends: [HttpBackend, HttpBackend],
        backendOptions: [remoteBackend, localBackend],
      },
      react: {
        useSuspense: true,
      },
    });
}

/**
 * For use with the external i18n server. Upon first run it fetches manifest file (created as part of the build
 * process by Vite) and parses it into a map of language code to file URL.
 *
 * @param manifest The current map holding namespace and language code to file URL
 * @return The populated manifest file, or undefined if an error occurs
 */
export async function fetchI18nManifest(manifest: Record<string, Record<string, string>>) {
  if (!_.isEmpty(manifest)) {
    return manifest;
  }

  try {
    const response = await fetch(`/manifest-i18n.json`, { method: 'GET' });
    if (response.status !== HttpCodes.OK) {
      throw new Error(`Failed to fetch i18n manifest: ${response.status}`);
    }

    return await response.json();
  } catch (e) {
    return manifest;
  }
}

/**
 * Return only the language portion of the locale string. For example, a locale of "pt-BR" would return "pt".
 *
 * @param locale - The locale
 */
export function getLanguage(locale: LocaleKey) {
  return locale.slice(0, 2);
}

export function getBrowserLanguage() {
  return navigator.language || (navigator as any).userLanguage;
}

export const safeChangeLanguage = async (preferredLocale: LocaleKey | null) => {
  const changeLanguage = async () => {
    await i18next.changeLanguage(preferredLocale ?? LOCALES.EN);
  };

  if (i18next.isInitialized) {
    await changeLanguage();
  } else {
    console.warn(
      '`i18next` not initialized when safeChangeLanguage called - this should not happen with safeguards in' +
        ' top-level `Application.page` component',
    );
    i18next.on('initialized', changeLanguage);
    return () => {
      i18next.off('initialized', changeLanguage);
    };
  }
};

/**
 * Switches to the provided language. This function also loads the appropriate
 * Highcharts translations file, and makes sure that the Tools are properly translated.
 *
 * @param locale - The locale key
 */
export async function switchLanguage(locale: LocaleKey) {
  await safeChangeLanguage(locale);
  localStorage.setItem('seeq_preferred_locale', locale);
  Highcharts.setOptions({ lang: i18next.t('HIGHCHARTS', { returnObjects: true }) as LangOptions });
  // NOTE: we do not want to set a new locale for moment as users are used to having it use their browser
  // locale (CRAB-24568)
  flux.dispatch('INVESTIGATE_TRIGGER_INVESTIGATE_TOOL_TRANSLATION');
  flux.dispatch('SET_USER_LANGUAGE', locale);
  if (locale !== LOCALES.EN && _.isEmpty(sqWorkbenchStore.agGridLocale)) {
    // Unfortunately individual locales cannot be loaded separately
    const localeData = await import('@seeqdev/ag/locale');
    setAgGridLocale({
      [LOCALES.ES]: localeData.AG_GRID_LOCALE_ES,
      [LOCALES.DE]: localeData.AG_GRID_LOCALE_DE,
      [LOCALES.FR]: localeData.AG_GRID_LOCALE_FR,
      [LOCALES.IT]: localeData.AG_GRID_LOCALE_IT,
      [LOCALES.JA]: localeData.AG_GRID_LOCALE_JP,
      [LOCALES.KO]: localeData.AG_GRID_LOCALE_KR,
      [LOCALES.NL]: localeData.AG_GRID_LOCALE_NL,
      [LOCALES.PT]: localeData.AG_GRID_LOCALE_PT,
      [LOCALES.ZH]: localeData.AG_GRID_LOCALE_CN,
      [LOCALES.EN]: localeData.AG_GRID_LOCALE_EN,
    });
  }
}

/**
 * Vantage uses react-date-picker which requires date-fns for date formatting.
 * This list should be kept up to date with the moment import lists.
 */
type DateFnsLocaleData = { [key in string]: () => Promise<any> };
export const DATE_FNS_LOCALE_DATA: DateFnsLocaleData = {
  'af': () => import('date-fns/locale/af/index.js'),
  'ar-dz': () => import('date-fns/locale/ar-DZ/index.js'),
  'ar-ma': () => import('date-fns/locale/ar-MA/index.js'),
  'ar-sa': () => import('date-fns/locale/ar-SA/index.js'),
  'az': () => import('date-fns/locale/az/index.js'),
  'be': () => import('date-fns/locale/be/index.js'),
  'bg': () => import('date-fns/locale/bg/index.js'),
  'bn': () => import('date-fns/locale/bn/index.js'),
  'ca': () => import('date-fns/locale/ca/index.js'),
  'cs': () => import('date-fns/locale/cs/index.js'),
  'cy': () => import('date-fns/locale/cy/index.js'),
  'da': () => import('date-fns/locale/da/index.js'),
  'de': () => import('date-fns/locale/de/index.js'),
  'el': () => import('date-fns/locale/el/index.js'),
  'en-au': () => import('date-fns/locale/en-AU/index.js'),
  'en-ca': () => import('date-fns/locale/en-CA/index.js'),
  'en-gb': () => import('date-fns/locale/en-GB/index.js'),
  'en-in': () => import('date-fns/locale/en-IN/index.js'),
  'en-nz': () => import('date-fns/locale/en-NZ/index.js'),
  'eo': () => import('date-fns/locale/eo/index.js'),
  'es': () => import('date-fns/locale/es/index.js'),
  'et': () => import('date-fns/locale/et/index.js'),
  'eu': () => import('date-fns/locale/eu/index.js'),
  'fi': () => import('date-fns/locale/fi/index.js'),
  'fr-ca': () => import('date-fns/locale/fr-CA/index.js'),
  'fr-ch': () => import('date-fns/locale/fr-CH/index.js'),
  'fr': () => import('date-fns/locale/fr/index.js'),
  'gd': () => import('date-fns/locale/gd/index.js'),
  'gl': () => import('date-fns/locale/gl/index.js'),
  'gu': () => import('date-fns/locale/gu/index.js'),
  'he': () => import('date-fns/locale/he/index.js'),
  'hi': () => import('date-fns/locale/hi/index.js'),
  'hr': () => import('date-fns/locale/hr/index.js'),
  'hu': () => import('date-fns/locale/hu/index.js'),
  'id': () => import('date-fns/locale/id/index.js'),
  'is': () => import('date-fns/locale/is/index.js'),
  'it': () => import('date-fns/locale/it/index.js'),
  'ja': () => import('date-fns/locale/ja/index.js'),
  'ka': () => import('date-fns/locale/ka/index.js'),
  'kk': () => import('date-fns/locale/kk/index.js'),
  'kn': () => import('date-fns/locale/kn/index.js'),
  'ko': () => import('date-fns/locale/ko/index.js'),
  'lt': () => import('date-fns/locale/lt/index.js'),
  'lv': () => import('date-fns/locale/lv/index.js'),
  'mk': () => import('date-fns/locale/mk/index.js'),
  'ms': () => import('date-fns/locale/ms/index.js'),
  'mt': () => import('date-fns/locale/mt/index.js'),
  'nb': () => import('date-fns/locale/nb/index.js'),
  'nl-be': () => import('date-fns/locale/nl-BE/index.js'),
  'nl': () => import('date-fns/locale/nl/index.js'),
  'nn': () => import('date-fns/locale/nn/index.js'),
  'pl': () => import('date-fns/locale/pl/index.js'),
  'pt-br': () => import('date-fns/locale/pt-BR/index.js'),
  'pt': () => import('date-fns/locale/pt/index.js'),
  'ro': () => import('date-fns/locale/ro/index.js'),
  'ru': () => import('date-fns/locale/ru/index.js'),
  'sk': () => import('date-fns/locale/sk/index.js'),
  'sl': () => import('date-fns/locale/sl/index.js'),
  'sr': () => import('date-fns/locale/sr/index.js'),
  'sv': () => import('date-fns/locale/sv/index.js'),
  'ta': () => import('date-fns/locale/ta/index.js'),
  'te': () => import('date-fns/locale/te/index.js'),
  'th': () => import('date-fns/locale/th/index.js'),
  'tr': () => import('date-fns/locale/tr/index.js'),
  'uk': () => import('date-fns/locale/uk/index.js'),
  'uz': () => import('date-fns/locale/uz/index.js'),
  'vi': () => import('date-fns/locale/vi/index.js'),
  'zh-cn': () => import('date-fns/locale/zh-CN/index.js'),
  'zh-tw': () => import('date-fns/locale/zh-TW/index.js'),
};

/**
 * Used for setting  Moment's locale to match the user's browser settings so that dates
 * appear formatted in an expected way.
 *
 * Example: users with their browser set to UK English are in the en-gb locale
 * and expect to see "May 22nd, 2024" as "22/5/2024".
 **/

type MomentLocaleData = { [key in string]: any };
export const MOMENT_LOCALE_DATA: MomentLocaleData = {
  'af': () => import('moment/dist/locale/af'),
  'ar-dz': () => import('moment/dist/locale/ar-dz'),
  'ar-ma': () => import('moment/dist/locale/ar-ma'),
  'ar-sa': () => import('moment/dist/locale/ar-sa'),
  'az': () => import('moment/dist/locale/az'),
  'be': () => import('moment/dist/locale/be'),
  'bg': () => import('moment/dist/locale/bg'),
  'bn': () => import('moment/dist/locale/bn'),
  'ca': () => import('moment/dist/locale/ca'),
  'cs': () => import('moment/dist/locale/cs'),
  'cy': () => import('moment/dist/locale/cy'),
  'da': () => import('moment/dist/locale/da'),
  'de': () => import('moment/dist/locale/de'),
  'el': () => import('moment/dist/locale/el'),
  'en-au': () => import('moment/dist/locale/en-au'),
  'en-ca': () => import('moment/dist/locale/en-ca'),
  'en-gb': () => import('moment/dist/locale/en-gb'),
  'en-in': () => import('moment/dist/locale/en-in'),
  'en-nz': () => import('moment/dist/locale/en-nz'),
  'eo': () => import('moment/dist/locale/eo'),
  'es': () => import('moment/dist/locale/es'),
  'et': () => import('moment/dist/locale/et'),
  'eu': () => import('moment/dist/locale/eu'),
  'fi': () => import('moment/dist/locale/fi'),
  'fr-ca': () => import('moment/dist/locale/fr-ca'),
  'fr-ch': () => import('moment/dist/locale/fr-ch'),
  'fr': () => import('moment/dist/locale/fr'),
  'gd': () => import('moment/dist/locale/gd'),
  'gl': () => import('moment/dist/locale/gl'),
  'gu': () => import('moment/dist/locale/gu'),
  'he': () => import('moment/dist/locale/he'),
  'hi': () => import('moment/dist/locale/hi'),
  'hr': () => import('moment/dist/locale/hr'),
  'hu': () => import('moment/dist/locale/hu'),
  'id': () => import('moment/dist/locale/id'),
  'is': () => import('moment/dist/locale/is'),
  'it': () => import('moment/dist/locale/it'),
  'ja': () => import('moment/dist/locale/ja'),
  'ka': () => import('moment/dist/locale/ka'),
  'kk': () => import('moment/dist/locale/kk'),
  'kn': () => import('moment/dist/locale/kn'),
  'ko': () => import('moment/dist/locale/ko'),
  'lt': () => import('moment/dist/locale/lt'),
  'lv': () => import('moment/dist/locale/lv'),
  'mk': () => import('moment/dist/locale/mk'),
  'ms': () => import('moment/dist/locale/ms'),
  'mt': () => import('moment/dist/locale/mt'),
  'nb': () => import('moment/dist/locale/nb'),
  'nl-be': () => import('moment/dist/locale/nl-be'),
  'nl': () => import('moment/dist/locale/nl'),
  'nn': () => import('moment/dist/locale/nn'),
  'pl': () => import('moment/dist/locale/pl'),
  'pt-br': () => import('moment/dist/locale/pt-br'),
  'pt': () => import('moment/dist/locale/pt'),
  'ro': () => import('moment/dist/locale/ro'),
  'ru': () => import('moment/dist/locale/ru'),
  'sk': () => import('moment/dist/locale/sk'),
  'sl': () => import('moment/dist/locale/sl'),
  'sr': () => import('moment/dist/locale/sr'),
  'sv': () => import('moment/dist/locale/sv'),
  'ta': () => import('moment/dist/locale/ta'),
  'te': () => import('moment/dist/locale/te'),
  'th': () => import('moment/dist/locale/th'),
  'tr': () => import('moment/dist/locale/tr'),
  'uk': () => import('moment/dist/locale/uk'),
  'uz': () => import('moment/dist/locale/uz'),
  'vi': () => import('moment/dist/locale/vi'),
  'zh-cn': () => import('moment/dist/locale/zh-cn'),
  'zh-tw': () => import('moment/dist/locale/zh-tw'),
} as const;

type FlatpickrLocaleData = { [key in string]: () => Promise<any> };
export const FLATPICKR_LOCALE_DATA: FlatpickrLocaleData = {
  'ar-DZ': () => import('flatpickr/dist/l10n/ar-dz'),
  'ar': () => import('flatpickr/dist/l10n/ar'),
  'de-AT': () => import('flatpickr/dist/l10n/at'), // Austrian German
  'az': () => import('flatpickr/dist/l10n/az'),
  'be': () => import('flatpickr/dist/l10n/be'),
  'bg': () => import('flatpickr/dist/l10n/bg'),
  'bn': () => import('flatpickr/dist/l10n/bn'),
  'bs': () => import('flatpickr/dist/l10n/bs'),
  'ca': () => import('flatpickr/dist/l10n/cat'),
  'ckb': () => import('flatpickr/dist/l10n/ckb'),
  'cs': () => import('flatpickr/dist/l10n/cs'),
  'cy': () => import('flatpickr/dist/l10n/cy'),
  'da': () => import('flatpickr/dist/l10n/da'),
  'de': () => import('flatpickr/dist/l10n/de'),
  'eo': () => import('flatpickr/dist/l10n/eo'),
  'es': () => import('flatpickr/dist/l10n/es'),
  'et': () => import('flatpickr/dist/l10n/et'),
  'fa': () => import('flatpickr/dist/l10n/fa'),
  'fi': () => import('flatpickr/dist/l10n/fi'),
  'fo': () => import('flatpickr/dist/l10n/fo'),
  'fr': () => import('flatpickr/dist/l10n/fr'),
  'ga': () => import('flatpickr/dist/l10n/ga'),
  'el': () => import('flatpickr/dist/l10n/gr'), // Greek
  'he': () => import('flatpickr/dist/l10n/he'),
  'hi': () => import('flatpickr/dist/l10n/hi'),
  'hr': () => import('flatpickr/dist/l10n/hr'),
  'hu': () => import('flatpickr/dist/l10n/hu'),
  'hy': () => import('flatpickr/dist/l10n/hy'),
  'id': () => import('flatpickr/dist/l10n/id'),
  'is': () => import('flatpickr/dist/l10n/is'),
  'it': () => import('flatpickr/dist/l10n/it'),
  'ja': () => import('flatpickr/dist/l10n/ja'),
  'ka': () => import('flatpickr/dist/l10n/ka'),
  'km': () => import('flatpickr/dist/l10n/km'),
  'ko': () => import('flatpickr/dist/l10n/ko'),
  'kk': () => import('flatpickr/dist/l10n/kz'), // Kazakh
  'lt': () => import('flatpickr/dist/l10n/lt'),
  'lv': () => import('flatpickr/dist/l10n/lv'),
  'mk': () => import('flatpickr/dist/l10n/mk'),
  'mn': () => import('flatpickr/dist/l10n/mn'),
  'ms': () => import('flatpickr/dist/l10n/ms'),
  'my': () => import('flatpickr/dist/l10n/my'),
  'nl': () => import('flatpickr/dist/l10n/nl'),
  'nn': () => import('flatpickr/dist/l10n/nn'),
  'no': () => import('flatpickr/dist/l10n/no'),
  'pa': () => import('flatpickr/dist/l10n/pa'),
  'pl': () => import('flatpickr/dist/l10n/pl'),
  'pt': () => import('flatpickr/dist/l10n/pt'),
  'ro': () => import('flatpickr/dist/l10n/ro'),
  'ru': () => import('flatpickr/dist/l10n/ru'),
  'si': () => import('flatpickr/dist/l10n/si'),
  'sk': () => import('flatpickr/dist/l10n/sk'),
  'sl': () => import('flatpickr/dist/l10n/sl'),
  'sq': () => import('flatpickr/dist/l10n/sq'),
  'sr-Cyrl': () => import('flatpickr/dist/l10n/sr-cyr'),
  'sr': () => import('flatpickr/dist/l10n/sr'),
  'sv': () => import('flatpickr/dist/l10n/sv'),
  'th': () => import('flatpickr/dist/l10n/th'),
  'tr': () => import('flatpickr/dist/l10n/tr'),
  'uk': () => import('flatpickr/dist/l10n/uk'),
  'uz-Latn': () => import('flatpickr/dist/l10n/uz_latn'),
  'uz': () => import('flatpickr/dist/l10n/uz'),
  'vi': () => import('flatpickr/dist/l10n/vn'), // Vietnamese
  'zh-TW': () => import('flatpickr/dist/l10n/zh-tw'),
  'zh': () => import('flatpickr/dist/l10n/zh'),
};

export const getAgGridKey = (key: string) => key.toUpperCase().replace(/\s+/g, '_');
